import { useState, useEffect, useCallback } from "react";
import { C, fmt, Btn, Input, Select, Textarea, StatCard, Tabs, useIsMobile, SEMILLAS_OPC } from "./ui";
import { distribucionFardoService } from "../services/supabase";
import { toast } from "../services/toast";

export function FardosPanel({ fardosState, potreros, user, profile }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("registro");
  const tabs = [
    { id: "registro", label: "📦 Registro" },
    { id: "distribucion", label: "🚚 Distribución" },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>
          Fardos
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
          Registro de fardos y distribución a potreros. Guardado en base de datos.
        </div>

        <Tabs tabs={tabs} active={tab} onChange={setTab} />

        {tab === "registro" && <TabRegistro fardosState={fardosState} potreros={potreros} isMobile={isMobile} />}
        {tab === "distribucion" && <TabDistribucion fardosState={fardosState} potreros={potreros} />}
      </div>
    </div>
  );
}

// ─── TAB REGISTRO ─────────────────────────────────────────
function TabRegistro({ fardosState, potreros, isMobile }) {
  const { fardos, createFardo, deleteFardo } = fardosState;
  const [show, setShow] = useState(false);
  const [pasto, setPasto] = useState("");
  const [tipo, setTipo] = useState("Redondo");
  const [cantidad, setCantidad] = useState("");
  const [pesoPromedio, setPesoPromedio] = useState("");
  const [potreroId, setPotreroId] = useState(potreros[0]?.id || "");
  const [packer, setPacker] = useState("");
  const [obs, setObs] = useState("");
  const [confirmar, setConfirmar] = useState(null);

  const guardar = async () => {
    if (!pasto || !cantidad) return;
    try {
      await createFardo({
        pasto_principal: pasto,
        tipo,
        cantidad: parseInt(cantidad) || 0,
        peso_promedio: parseFloat(pesoPromedio) || 0,
        potrero_id: potreroId || null,
        packer,
        obs,
        estado: "disponible",
      });
      setShow(false); setPasto(""); setCantidad(""); setPesoPromedio(""); setPacker(""); setObs("");
      toast.success("Fardo registrado.");
    } catch (e) {
      console.error("Error guardando fardo:", e);
      toast.error("No se pudo guardar el fardo: " + (e.message || "error"));
    }
  };

  const eliminar = async (id) => {
    try { await deleteFardo(id); setConfirmar(null); } catch (e) { console.error("Error eliminando fardo:", e); }
  };

  const totalFardos = fardos.reduce((s, f) => s + (f.cantidad || 0), 0);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(100px,1fr))", gap: 8, margin: "14px 0 16px" }}>
        <StatCard val={totalFardos} label="fardos disponibles" />
        <StatCard val={fardos.length} label="lotes" />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <Btn variant="primary" onClick={() => setShow(!show)} disabled={potreros.length === 0}>
          {show ? "Cancelar" : "+ Registrar fardo"}
        </Btn>
      </div>

      {show && (
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${C.arenaDark}` }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0 12px" }}>
            <Select label="Pasto principal" value={pasto} onChange={setPasto} options={[...SEMILLAS_OPC]} />
            <Select label="Tipo" value={tipo} onChange={setTipo} options={["Redondo", "Fardote", "Cuadrado"]} />
            <Input label="Cantidad de fardos" value={cantidad} onChange={setCantidad} type="number" placeholder="0" />
            <Input label="Peso promedio (kg)" value={pesoPromedio} onChange={setPesoPromedio} type="number" placeholder="0" />
            <Select label="Potrero (origen)" value={potreroId} onChange={setPotreroId}
              options={potreros.map(p => ({ v: p.id, l: p.nombre }))} />
            <Input label="Packer" value={packer} onChange={setPacker} placeholder="Empresa que enfardó" />
          </div>
          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2} />
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Btn variant="ghost" size="sm" onClick={() => setShow(false)} style={{ flex: 1 }}>Cancelar</Btn>
            <Btn variant="primary" size="sm" onClick={guardar} disabled={!pasto || !cantidad} style={{ flex: 1 }}>Guardar</Btn>
          </div>
        </div>
      )}

      {fardos.length === 0 && !show && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
          <div style={{ fontSize: 13 }}>Registrá fardos enfardados para llevar control del stock.</div>
        </div>
      )}

      {fardos.map((f) => (
        <div key={f.id} style={{ background: C.white, borderRadius: 10, padding: "10px 14px", marginBottom: 7, border: `1px solid ${C.arenaDark}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20 }}>📦</span>
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.tierra }}>{f.pasto_principal}</div>
              <div style={{ fontSize: 11, color: C.muted }}>
                {f.tipo} · {f.cantidad} disponibles · {f.peso_promedio} kg promedio
                {f.packer && ` · ${f.packer}`}
              </div>
            </div>
            {confirmar === f.id ? (
              <>
                <button onClick={() => eliminar(f.id)} style={{ ...btnF, background: C.danger, color: "#fff", borderColor: C.danger }}>Sí</button>
                <button onClick={() => setConfirmar(null)} style={btnF}>No</button>
              </>
            ) : (
              <button onClick={() => setConfirmar(f.id)} style={{ ...btnF, color: C.danger, borderColor: "#E0909055" }}>✕</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── TAB DISTRIBUCIÓN ─────────────────────────────────────
function TabDistribucion({ fardosState, potreros }) {
  const { fardos, updateFardo } = fardosState;
  const [fardoSelId, setFardoSelId] = useState("");
  const [tipoDistrib, setTipoDistrib] = useState("potrero"); // "potrero" | "animales"
  const [potreroSel, setPotreroSel] = useState("");
  const [animalSel, setAnimalSel] = useState("");
  const [cantDist, setCantDist] = useState("");
  const [obs, setObs] = useState("");
  const [distribuciones, setDistribuciones] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const disponibles = fardos.filter(f => (f.cantidad || 0) > 0);
  const fardoSel = fardos.find(f => f.id === fardoSelId);
  const maxDist = fardoSel?.cantidad || 0;
  const nDist = Math.min(parseInt(cantDist) || 0, maxDist);
  const vivosDe = (p) => (p?.animalesAsignados || []).filter(a => (a.estado || "activo") === "activo").length;
  const nombrePotrero = (id) => potreros.find(p => p.id === id)?.nombre || "—";

  const cargarDist = useCallback(async (fardoId) => {
    if (!fardoId) { setDistribuciones([]); return; }
    try {
      const data = await distribucionFardoService.getByFardo(fardoId);
      setDistribuciones(data || []);
    } catch (e) {
      console.error("Error cargando distribuciones:", e);
    }
  }, []);

  useEffect(() => { cargarDist(fardoSelId); }, [fardoSelId, cargarDist]);

  const distribuir = async () => {
    if (!fardoSel || nDist <= 0 || guardando) return;
    if (tipoDistrib === "potrero" && !potreroSel) return;
    if (tipoDistrib === "animales" && !animalSel) return;

    setGuardando(true);
    try {
      const payload = {
        cantidad: nDist,
        fecha: new Date().toISOString().slice(0, 10),
        obs: obs || null,
      };
      if (tipoDistrib === "potrero") {
        payload.potrero_id = potreroSel;
      } else {
        payload.animal_id = animalSel;
      }

      await distribucionFardoService.create(fardoSel.id, payload);
      const restante = maxDist - nDist;
      await updateFardo(fardoSel.id, { cantidad: restante, estado: restante > 0 ? "disponible" : "distribuido" });
      setPotreroSel(""); setAnimalSel(""); setCantDist(""); setObs("");
      await cargarDist(fardoSel.id);
      toast.success(`${nDist} fardo${nDist !== 1 ? "s" : ""} distribuido${nDist !== 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error("No se pudo distribuir: " + (e.message || "error"));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
        Elegí un fardo con stock y repartí una cantidad hacia un potrero. Se descuenta del stock y queda en el historial.
      </div>

      <Select label="Fardo disponible" value={fardoSelId}
        onChange={(v) => { setFardoSelId(v); const f = fardos.find(x => x.id === v); setCantDist(f ? String(f.cantidad) : ""); }}
        options={[{ v: "", l: "Seleccionar fardo..." }, ...disponibles.map(f => ({
          v: f.id, l: `${f.pasto_principal} (${f.cantidad} ${f.tipo} disponibles)`
        }))]} />

      {fardoSel && (
        <>
          <div style={{ background: C.arena, borderRadius: 9, padding: 12, marginBottom: 14, borderLeft: `4px solid ${C.pasto}` }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: C.tierra }}>{fardoSel.pasto_principal}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2, marginBottom: 10 }}>
              {fardoSel.cantidad} fardos {fardoSel.tipo} disponibles
              {fardoSel.peso_promedio > 0 ? ` · ${(fardoSel.cantidad * fardoSel.peso_promedio).toLocaleString("es-UY")} kg` : ""}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: C.tierra, fontWeight: 600 }}>¿Cuántos distribuir?</span>
              <input type="number" value={cantDist} min={1} max={maxDist} onChange={e => setCantDist(e.target.value)}
                style={{ width: 80, padding: "7px 9px", border: `1.5px solid ${C.arenaDark}`, borderRadius: 7,
                  fontSize: 15, fontWeight: 700, textAlign: "right", color: C.tierra, background: C.white }} />
              <span style={{ fontSize: 12, color: C.muted }}>de {maxDist}</span>
            </div>
          </div>

          <Select label="Tipo de distribución" value={tipoDistrib} onChange={setTipoDistrib}
            options={[
              {v: "potrero", l: "📍 Por potrero"},
              {v: "animales", l: "🐄 Por animal"}
            ]} />

          {tipoDistrib === "potrero" && (
            <Select label="Potrero destino" value={potreroSel} onChange={setPotreroSel}
              options={[{ v: "", l: "Elegí potrero..." }, ...potreros.map(p => ({
                v: p.id, l: `${p.nombre} · 🐄 ${vivosDe(p)} animales${p.ha ? ` · ${p.ha.toFixed(1)} ha` : ""}`
              }))]} />
          )}

          {tipoDistrib === "animales" && (
            <>
              <Select label="Potrero" value={potreroSel} onChange={(v) => { setPotreroSel(v); setAnimalSel(""); }}
                options={[{ v: "", l: "Elegí potrero..." }, ...potreros.map(p => ({
                  v: p.id, l: `${p.nombre} · 🐄 ${vivosDe(p)} animales`
                }))]} />
              {potreroSel && (
                <Select label="Animal" value={animalSel} onChange={setAnimalSel}
                  options={[
                    { v: "", l: "Elegí animal..." },
                    ...((potreros.find(p => p.id === potreroSel)?.animalesAsignados || [])
                      .filter(a => (a.estado || "activo") === "activo")
                      .map(a => ({
                        v: a.id, l: `#${a.caravana || "—"} · ${a.categoria} (${a.raza || "sin raza"})`
                      })))
                  ]} />
              )}
            </>
          )}

          {potreroSel && nDist > 0 && (
            <div style={{ background: C.pastoLight + "22", borderRadius: 8, padding: 12, marginBottom: 12, borderLeft: `4px solid ${C.pasto}`, fontSize: 12, color: C.tierra }}>
              {nDist} fardo{nDist !== 1 ? "s" : ""} → <b>{nombrePotrero(potreroSel)}</b>
              {vivosDe(potreros.find(p => p.id === potreroSel)) > 0 &&
                <> · {(nDist / vivosDe(potreros.find(p => p.id === potreroSel))).toFixed(1)} fardos/animal</>}
            </div>
          )}

          <Textarea label="Observaciones (opcional)" value={obs} onChange={setObs} rows={2} />

          <Btn variant="primary" onClick={distribuir} disabled={!potreroSel || nDist <= 0 || guardando} style={{ width: "100%" }}>
            {guardando ? "Distribuyendo…" : `Distribuir ${nDist} fardo${nDist !== 1 ? "s" : ""} al potrero`}
          </Btn>

          {/* Historial de distribuciones de este fardo */}
          <div style={{ marginTop: 16, borderTop: `1px solid ${C.arena}`, paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 8, fontWeight: 600 }}>
              Historial de este fardo
            </div>
            {distribuciones.length === 0 ? (
              <div style={{ fontSize: 12, color: C.muted }}>Todavía no se distribuyó.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {distribuciones.map(d => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, background: C.fondo, borderRadius: 6, padding: "6px 10px" }}>
                    <span style={{ fontSize: 15 }}>🚚</span>
                    <span style={{ color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>{fmt(d.fecha)}</span>
                    <span style={{ flex: 1 }}>📍 {nombrePotrero(d.potrero_id)}{d.obs ? ` · ${d.obs}` : ""}</span>
                    <span style={{ fontWeight: 700, color: C.tierra }}>{d.cantidad}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const btnF = {
  background: C.arena, border: `1px solid ${C.arenaDark}`, borderRadius: 6,
  padding: "6px 9px", fontSize: 12, cursor: "pointer", color: C.tierra, fontFamily: "monospace", flexShrink: 0,
};
